import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Box,
  IconButton,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { QRCodeSVG } from "qrcode.react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import LocationMap from "../Map/LocationMap";
import PDFViewer from "../PDF/PDFViewer";
import SlideshowWithModal from "../Slideshow/SlideshowWithModal";
import { useAuth } from "../../contexts/JWTAuthContext";
import { useNavigate } from "react-router-dom";
import { getUnits } from "../../Services/unitsService";
import videoMP4 from "../../Assets/npm/Final video.mp4";
import UnitInfoCard from "./UnitInfoCard";
import { ImageBaseUrl } from "../../Services/config";

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  borderRadius: "12px !important",
  marginBottom: theme.spacing(2),
  boxShadow: "0 2px 8px rgba(70, 80, 100, 0.1)",
  border: "1px solid #e9ecef",
  overflow: "hidden",
  "&:before": {
    display: "none",
  },
  "&.Mui-expanded": {
    margin: theme.spacing(2, 0),
  },
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  background: "#465064",
  color: "white",
  minHeight: "60px !important",
  padding: theme.spacing(0, 3),
  "& .MuiAccordionSummary-content": {
    margin: theme.spacing(1.5, 0),
  },
  "& .MuiAccordionSummary-expandIconWrapper": {
    color: "white",
    transform: "rotate(0deg)",
    transition: "transform 0.2s ease",
    "&.Mui-expanded": {
      transform: "rotate(180deg)",
    },
  },
  "&:hover": {
    background: "#3a4254",
  },
}));

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  padding: theme.spacing(3),
  background: "#f8f9fa",
}));

const LinkBox = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  background: "white",
  borderRadius: theme.spacing(1),
  marginTop: theme.spacing(2),
  width: "100%",
  maxWidth: "500px",
  boxShadow: "0 2px 8px rgba(70, 80, 100, 0.1)",
  border: "1px solid #e9ecef",
}));

// Local Video Player Component for MP4 files
const LocalVideoPlayer = ({ videoSrc, title = "Video" }) => {
  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        paddingBottom: "56.25%", // 16:9 aspect ratio
        height: 0,
        overflow: "hidden",
        borderRadius: 2,
        backgroundColor: "#000",
      }}
    >
      <video
        controls
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          objectFit: "contain",
        }}
        preload="metadata"
      >
        <source src={videoSrc} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    </Box>
  );
};

// Dynamically import all images from the npm assets folders
function importAll(r) {
  return r.keys().map(r);
}

// NPM specific assets — three brochure sets extracted from PDFs
const brochure1Images = importAll(
  require.context("../../Assets/npm/borchure pdf/brochure-1+1", false, /\.jpe?g$/i)
);
const brochure2Images = importAll(
  require.context("../../Assets/npm/borchure pdf/brochure-2+1", false, /\.jpe?g$/i)
);
const brochure3Images = importAll(
  require.context("../../Assets/npm/borchure pdf/brochure-property", false, /\.jpe?g$/i)
);

const salesPlanImages = importAll(
  require.context("../../Assets/npm/sales plan", false, /\.jpe?g$/i)
);

const elevationShots = importAll(
  require.context("../../Assets/npm/elevation shots", false, /\.jpe?g$/i)
);

const interiorShots = importAll(
  require.context("../../Assets/npm/interior shots", false, /\.(jpe?g|png)$/i)
);

const MediaNpm = ({
  imageMap = true,
  showQRCode = true,
  view = true,
  useSameTabForUnitPayment = false,
  salesId,
  sessionId,
  onLoadingChange,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const currentDomain = window.location.origin;
  const [qrValue, setQRvalue] = useState("");
  const [expanded, setExpanded] = useState(showQRCode ? "qr" : "brochure-1"); // Set default based on showQRCode prop
  const [units, setUnits] = useState([]); // State to store all units
  const [loading, setLoading] = useState(false); // Loading state for units
  const [location, setLocation] = useState({
    latitude: 30.04686121883895, // Default Cairo coordinates
    longitude: 31.52336850739469,
    title: "Our Location",
  });
  const [pdfUrl, setPdfUrl] = useState(""); // Add your PDF URL here

  // Handle accordion expansion
  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  // Handle QR code copy
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(qrValue);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  // Handle QR code open
  const handleOpen = () => {
    window.open(qrValue, "_blank", "noopener,noreferrer");
  };
  useEffect(() => {
    fetchUnits();
  }, []);
  // Fetch all units from API
  const fetchUnits = async () => {
    try {
      setLoading(true);
      if (onLoadingChange) onLoadingChange(true);
      const response = await getUnits({ pageSize: 1000, pageIndex: 0 }); // Fetch all units
      console.log("Fetched units:", response);
      setUnits(response.Data || []);
    } catch (error) {
      console.error("Error fetching units:", error);
    } finally {
      setLoading(false);
      if (onLoadingChange) onLoadingChange(false);
    }
  };

  const createSlideShowImageMaps = (images) => {
    console.log(images);
    return ["0", "1", "2", "3"].map((image) => {
      const unit = units.filter((unit) => image.includes(unit.ImageMape));
      const value = unit.map((e) => {
        return {
          shape: "rect",
          alt: e.UnitNumber,
          description: e.UnitNumber,
          highlightColor:
            e.UnitStatus === 0
              ? "rgba(255, 255, 0, 0)"
              : "rgba(255, 0, 0, 0.25)",
          percentageCoords: e.LocationOnMap,
          content:
            e.UnitStatus === 0 &&
            `
            <div style="padding: 20px; font-family: Arial, sans-serif;">
              <div style="background: #465064; color: white; padding: 15px; border-radius: 8px 8px 0 0; margin: -20px -20px 15px -20px;">
                <h3 style="margin: 0; font-size: 18px; font-weight: 600;">Unit ${
                  e.UnitNumber || "N/A"
                }</h3>
                <p style="margin: 5px 0 0 0; font-size: 14px; opacity: 0.8;">Building ${
                  e.BuildingNumber || "N/A"
                } - Floor ${e.Floor || "N/A"}</p>
              </div>
              
              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 15px;">
                <div style="background: #f8f9fa; padding: 10px; border-radius: 6px; border-left: 4px solid #465064;">
                  <div style="font-weight: bold; color: #495057; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px;">Area</div>
                  <div style="font-size: 16px; color: #212529; font-weight: 600;">${
                    e.Area || e.BUA || "N/A"
                  } m²</div>
                </div>
                
                <div style="background: #f8f9fa; padding: 10px; border-radius: 6px; border-left: 4px solid #28a745;">
                  <div style="font-weight: bold; color: #495057; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px;">BUA</div>
                  <div style="font-size: 16px; color: #212529; font-weight: 600;">${
                    e.BUA || "N/A"
                  } m²</div>
                </div>
              </div>
              
              <div style="background: #f8f9fa; padding: 15px; border-radius: 6px; margin-bottom: 15px;">
                <div style="font-weight: bold; color: #495057; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px;">Room Configuration</div>
                <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px;">
  
                  <div style="display: flex; align-items: center; gap: 5px;">
                    <span style="font-size: 14px;">${
                      e.Bathrooms || "0"
                    } Bathrooms</span>
                    <span style="display: flex; align-items: center; gap: 5px;">
                    <span style="font-size: 14px;">${
                      e.Bathrooms || "0"
                    } Bathrooms</span>
                  </span>
                  </div>
                </div>
              </div>
              
              ${
                e.UnitDescription
                  ? `
                <div style="background: #e3f2fd; padding: 12px; border-radius: 6px; border-left: 4px solid #2196f3; margin-bottom: 15px;">
                  <div style="font-weight: bold; color: #1976d2; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 5px;">Description</div>
                  <div style="font-size: 14px; color: #1565c0; line-height: 1.4;">${e.UnitDescription}</div>
                </div>
              `
                  : ""
              }
              
              ${
                e.UnitPrice
                  ? `
                <div style="background: linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%); padding: 15px; border-radius: 6px; text-align: center; margin-bottom: 15px;">
                  <div style="font-weight: bold; color: #d32f2f; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 5px;">Price</div>
                  <div style="font-size: 20px; color: #c62828; font-weight: bold;">$${e.UnitPrice.toLocaleString()}</div>
                </div>
              `
                  : ""
              }
              
              ${
                e.Files && e.Files.length > 0
                  ? `
                <div style="background: #f8f9fa; padding: 15px; border-radius: 6px; margin-bottom: 15px;">
                  <div style="font-weight: bold; color: #495057; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 15px;">Unit Documents (${
                    e.Files.length
                  })</div>
                  <div style="display: flex; flex-direction: column; gap: 15px;">
                    ${e.Files.map((fileName) => {
                      const isPdf =
                        fileName.toLowerCase().includes(".pdf") ||
                        fileName.toLowerCase().includes("pdf");
                      if (isPdf) {
                        return `
                          <div style="position: relative; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 12px rgba(0,0,0,0.15); background: white;">
                            <iframe 
                              src="${ImageBaseUrl}${fileName}" 
                              style="
                                width: 100%; 
                                height: 500px; 
                                border: none;
                                border-radius: 8px;
                                display: block;
                              "
                              title="Unit PDF Document"
                            ></iframe>
                            <div style="
                              position: absolute; 
                              top: 10px; 
                              right: 10px; 
                              background: rgba(0,0,0,0.7); 
                              color: white; 
                              padding: 4px 8px; 
                              border-radius: 12px; 
                              font-size: 11px;
                              pointer-events: none;
                            ">
                              📄 PDF Document
                            </div>
                            <div style="
                              position: absolute; 
                              bottom: 10px; 
                              right: 10px; 
                              background: #007bff; 
                              color: white; 
                              padding: 6px 12px; 
                              border-radius: 4px; 
                              font-size: 12px;
                              cursor: pointer;
                              text-decoration: none;
                              display: inline-block;
                            " onclick="window.open('${ImageBaseUrl}${fileName}', '_blank')">
                              Open PDF
                            </div>
                          </div>
                        `;
                      } else {
                        return `
                          <div style="position: relative; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 12px rgba(0,0,0,0.15); background: white;">
                            <img 
                              src="${ImageBaseUrl}${fileName}" 
                              alt="Unit Image" 
                              style="
                                width: 100%; 
                                height: auto; 
                                max-height: 400px;
                                object-fit: contain; 
                                border-radius: 8px;
                                cursor: pointer;
                                transition: all 0.3s ease;
                                display: block;
                              "
                              onclick="window.open('${ImageBaseUrl}${fileName}', '_blank')"
                              onmouseover="this.style.transform='scale(1.02)'; this.style.boxShadow='0 8px 25px rgba(0,0,0,0.2)'"
                              onmouseout="this.style.transform='scale(1)'; this.style.boxShadow='0 4px 12px rgba(0,0,0,0.15)'"
                              onerror="this.style.display='none'"
                            />
                            <div style="
                              position: absolute; 
                              top: 10px; 
                              right: 10px; 
                              background: rgba(0,0,0,0.7); 
                              color: white; 
                              padding: 4px 8px; 
                              border-radius: 12px; 
                              font-size: 11px;
                              pointer-events: none;
                            ">
                              🔍 Click to enlarge
                            </div>
                          </div>
                        `;
                      }
                    }).join("")}
                  </div>
                </div>
              `
                  : ""
              }
              
              <div style="text-align: center; margin-bottom: 15px;">
                <button 
                  onclick="${useSameTabForUnitPayment ? `window.location.href = '/#/unit-payment/${e.Id}/${view}'` : `window.open('/#/unit-payment/${e.Id}/${view}', '_blank')`}"
                  style="
                    background: #465064;
                    color: white;
                    border: none;
                    padding: 12px 24px;
                    border-radius: 6px;
                    font-size: 14px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    box-shadow: 0 2px 4px rgba(70, 80, 100, 0.1);
                  "
                  onmouseover="this.style.background='#3a4254'; this.style.boxShadow='0 4px 8px rgba(70, 80, 100, 0.2)'"
                  onmouseout="this.style.background='#465064'; this.style.boxShadow='0 2px 4px rgba(70, 80, 100, 0.1)'"
                >
                  💳 Go to Payment
                </button>
              </div>
              
              <div style="margin-top: 15px; padding-top: 15px; border-top: 1px solid #dee2e6; font-size: 11px; color: #6c757d; text-align: center;">
                Unit ID: ${e.Id || "N/A"}
              </div>
            </div>
          `,
        };
      });
      return value;
    });
  };

  const slideshowImageMaps = createSlideShowImageMaps(salesPlanImages);

  // Enhanced image maps for the slideshow with popup content
  const slideshowImageMaps2 = [
    // First image: S-Plan mobil (1)_page-0001.jpg
    [
      {
        shape: "rect",
        alt: "S-304",
        description: "S-304",
        highlightColor: "rgba(255, 255, 0, 0)",
        percentageCoords:   "55.80,62.90,59.60,65.90",
        content: `
            <div style="padding: 20px; font-family: Arial, sans-serif;">
              <div style="background: #465064; color: white; padding: 15px; border-radius: 8px 8px 0 0; margin: -20px -20px 15px -20px;">
                <h3 style="margin: 0; font-size: 18px; font-weight: 600;">Unit S-312</h3>
                <p style="margin: 5px 0 0 0; font-size: 14px; opacity: 0.8;">Building 312 - Floor 3</p>
              </div>
              
              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 15px;">
                <div style="background: #f8f9fa; padding: 10px; border-radius: 6px; border-left: 4px solid #465064;">
                  <div style="font-weight: bold; color: #495057; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px;">Area</div>
                  <div style="font-size: 16px; color: #212529; font-weight: 600;">50 m²</div>
                </div>
                
                <div style="background: #f8f9fa; padding: 10px; border-radius: 6px; border-left: 4px solid #28a745;">
                  <div style="font-weight: bold; color: #495057; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px;">BUA</div>
                  <div style="font-size: 16px; color: #212529; font-weight: 600;">50 m²</div>
                </div>
              </div>
              
              <div style="background: #f8f9fa; padding: 15px; border-radius: 6px; margin-bottom: 15px;">
                <div style="font-weight: bold; color: #495057; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px;">Room Configuration</div>
                <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px;">
  
                  <div style="display: flex; align-items: center; gap: 5px;">
                    <span style="color: #667eea;">🚿</span>
                    <span style="font-size: 14px;">2 Bathrooms</span>
                  </div>
                </div>
              </div>
              
              
              
              
              
              
              
              <div style="text-align: center; margin-bottom: 15px;">
                <button 
                  onclick="${useSameTabForUnitPayment ? `window.location.href = '/#/unit-payment/6/${view}'` : `window.open('/#/unit-payment/6/${view}', '_blank')`}"
                  style="
                    background: #465064;
                    color: white;
                    border: none;
                    padding: 12px 24px;
                    border-radius: 6px;
                    font-size: 14px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    box-shadow: 0 2px 4px rgba(70, 80, 100, 0.1);
                  "
                  onmouseover="this.style.background='#3a4254'; this.style.boxShadow='0 4px 8px rgba(70, 80, 100, 0.2)'"
                  onmouseout="this.style.background='#465064'; this.style.boxShadow='0 2px 4px rgba(70, 80, 100, 0.1)'"
                >
                  💳 Go to Payment
                </button>
              </div>
              
              <div style="margin-top: 15px; padding-top: 15px; border-top: 1px solid #dee2e6; font-size: 11px; color: #6c757d; text-align: center;">
                Unit ID: 6
              </div>
            </div>
          `,
      },
    ],
  ];
  console.log("slideshowImageMaps",slideshowImageMaps);
  // Image maps for brochure images
  const brochureImageMaps = [
    [
      // {
      //   shape: "rect",
      //   coords: "100,150,300,250",
      //   alt: "Clinic Overview",
      //   description: "Overview of our medical clinic and services.",
      //   content: `
      //     <div style="padding: 20px;">
      //       <h3>Clinic Overview</h3>
      //       <p>Welcome to our state-of-the-art medical clinic. We are committed to providing
      //       the highest quality healthcare services in a comfortable and professional environment.</p>
      //       <p><strong>Our Mission:</strong> To provide exceptional healthcare with compassion,
      //       innovation, and excellence.</p>
      //     </div>
      //   `
      // }
    ],
    [],
  ];

  useEffect(() => {
    if (showQRCode) {
      setExpanded((current) => (current ? current : "qr"));
    } else {
      setExpanded((current) =>
        current === "qr" ? "brochure-1" : current || "brochure-1"
      );
    }
  }, [showQRCode]);

  useEffect(() => {
    const resolvedSalesId = salesId ?? user?.UserId ?? user?.id ?? "";

    if (!resolvedSalesId) {
      setQRvalue("");
      return;
    }

    const params = new URLSearchParams();
    // params.set("salesId", resolvedSalesId);

    params.set("sessionId", sessionId);

    const queryString = params.toString();
    const qrUrl = `${currentDomain}/#/orientation/${resolvedSalesId}${
      queryString ? `?${queryString}` : ""
    }`;

    setQRvalue(qrUrl);
  }, [salesId, sessionId, user, currentDomain]);

  // Fetch units when component mounts
  useEffect(() => {
    fetchUnits();
  }, []);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: 2,
        px: { xs: 1, sm: 2, md: 3 },
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          background: "white",
          borderRadius: 2,
          boxShadow: "0 2px 8px rgba(70, 80, 100, 0.1)",
          overflow: "hidden",
          p: { xs: 2, md: 3 },
        }}
      >
        <Grid container spacing={3}>
          {/* QR Code Section - Only show if showQRCode prop is true */}
          {showQRCode ? (
            <Grid item size={{ xs: 12, md: 12 }}>
              <StyledAccordion
                expanded={expanded === "qr"}
                onChange={handleAccordionChange("qr")}
              >
                <StyledAccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="qr-content"
                  id="qr-header"
                >
                  <Typography
                    variant="h5"
                    component="h2"
                    sx={{
                      fontWeight: "bold",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    📱 QR Code
                  </Typography>
                </StyledAccordionSummary>
                <StyledAccordionDetails>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <Card
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        background: "white",
                        boxShadow: "0 2px 8px rgba(70, 80, 100, 0.1)",
                        mb: 2,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          my: 2,
                        }}
                      >
                        <QRCodeSVG
                          value={qrValue}
                          size={256}
                          level="H"
                          includeMargin={true}
                          imageSettings={{
                            src: "",
                            height: 24,
                            width: 24,
                            excavate: true,
                          }}
                        />
                      </Box>
                    </Card>
                    <LinkBox>
                      <Typography
                        variant="body2"
                        sx={{
                          flex: 1,
                          fontFamily: "monospace",
                          color: "#495057",
                        }}
                      >
                        {qrValue}
                      </Typography>
                      <IconButton
                        onClick={handleCopy}
                        color="primary"
                        size="small"
                        sx={{
                          width: "32px",
                          height: "32px",
                          "& .MuiSvgIcon-root": {
                            width: "20px",
                            height: "20px",
                          },
                          "&:hover": {
                            backgroundColor: "rgba(70, 80, 100, 0.1)",
                          },
                        }}
                      >
                        <ContentCopyIcon />
                      </IconButton>
                      <IconButton
                        onClick={handleOpen}
                        color="primary"
                        size="small"
                        sx={{
                          width: "32px",
                          height: "32px",
                          "& .MuiSvgIcon-root": {
                            width: "20px",
                            height: "20px",
                          },
                          "&:hover": {
                            backgroundColor: "rgba(70, 80, 100, 0.1)",
                          },
                        }}
                      >
                        <OpenInNewIcon />
                      </IconButton>
                    </LinkBox>
                  </Box>
                </StyledAccordionDetails>
              </StyledAccordion>
            </Grid>
          ) : (
            <Grid item size={{ xs: 12, md: 12 }}>
              <Typography
                variant="h5"
                component="h2"
                sx={{
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                No active session
              </Typography>
            </Grid>
          )}

          {/* Brochure 1+1 Section */}
          <Grid item size={{ xs: 12, md: 12 }}>
            <StyledAccordion
              expanded={expanded === "brochure-1"}
              onChange={handleAccordionChange("brochure-1")}
            >
              <StyledAccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="brochure1-content"
                id="brochure1-header"
              >
                <Typography variant="h5" component="h2" sx={{ fontWeight: "bold", display: "flex", alignItems: "center", gap: 1 }}>
                  📋 Brochure (1+1)
                </Typography>
              </StyledAccordionSummary>
              <StyledAccordionDetails>
                <Card sx={{ borderRadius: 2, overflow: "hidden", boxShadow: "0 2px 8px rgba(70, 80, 100, 0.1)", mb: 2 }}>
                  <SlideshowWithModal images={brochure1Images} interval={5000} autoPlay={false} width="100%" height="100%" usePercentageCoords={true} />
                </Card>
              </StyledAccordionDetails>
            </StyledAccordion>
          </Grid>

          {/* Brochure 2+1 Section */}
          <Grid item size={{ xs: 12, md: 12 }}>
            <StyledAccordion
              expanded={expanded === "brochure-2"}
              onChange={handleAccordionChange("brochure-2")}
            >
              <StyledAccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="brochure2-content"
                id="brochure2-header"
              >
                <Typography variant="h5" component="h2" sx={{ fontWeight: "bold", display: "flex", alignItems: "center", gap: 1 }}>
                  📋 Brochure (2+1)
                </Typography>
              </StyledAccordionSummary>
              <StyledAccordionDetails>
                <Card sx={{ borderRadius: 2, overflow: "hidden", boxShadow: "0 2px 8px rgba(70, 80, 100, 0.1)", mb: 2 }}>
                  <SlideshowWithModal images={brochure2Images} interval={5000} autoPlay={false} width="100%" height="100%" usePercentageCoords={true} />
                </Card>
              </StyledAccordionDetails>
            </StyledAccordion>
          </Grid>

          {/* Brochure Property Section */}
          <Grid item size={{ xs: 12, md: 12 }}>
            <StyledAccordion
              expanded={expanded === "brochure-3"}
              onChange={handleAccordionChange("brochure-3")}
            >
              <StyledAccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="brochure3-content"
                id="brochure3-header"
              >
                <Typography variant="h5" component="h2" sx={{ fontWeight: "bold", display: "flex", alignItems: "center", gap: 1 }}>
                  📋 Najma Property Brochure
                </Typography>
              </StyledAccordionSummary>
              <StyledAccordionDetails>
                <Card sx={{ borderRadius: 2, overflow: "hidden", boxShadow: "0 2px 8px rgba(70, 80, 100, 0.1)", mb: 2 }}>
                  <SlideshowWithModal images={brochure3Images} interval={5000} autoPlay={false} width="100%" height="100%" usePercentageCoords={true} />
                </Card>
              </StyledAccordionDetails>
            </StyledAccordion>
          </Grid>

          {/* Sales Plan Section */}
          <Grid item size={{ xs: 12, md: 12 }}>
            <StyledAccordion
              expanded={expanded === "sales-plan"}
              onChange={handleAccordionChange("sales-plan")}
            >
              <StyledAccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="sales-plan-content"
                id="sales-plan-header"
              >
                <Typography
                  variant="h5"
                  component="h2"
                  sx={{
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  🏢 Sales Plan & Unit Layouts
                </Typography>
              </StyledAccordionSummary>
              <StyledAccordionDetails>
                <Card
                  sx={{
                    borderRadius: 2,
                    overflow: "hidden",
                    boxShadow: "0 2px 8px rgba(70, 80, 100, 0.1)",
                  }}
                >
                  <SlideshowWithModal
                    images={salesPlanImages}
                    interval={5000}
                    width="100%"
                    height="100%"
                    autoPlay={false}
                    imageMaps={imageMap ? slideshowImageMaps : []}
                    usePercentageCoords={true}
                  />
                </Card>
              </StyledAccordionDetails>
            </StyledAccordion>
          </Grid>

          {/* Elevation Shots Section */}
          {elevationShots && elevationShots.length > 0 && (
            <Grid item size={{ xs: 12, md: 12 }}>
              <StyledAccordion
                expanded={expanded === "elevation"}
                onChange={handleAccordionChange("elevation")}
              >
                <StyledAccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="elevation-content"
                  id="elevation-header"
                >
                  <Typography
                    variant="h5"
                    component="h2"
                    sx={{
                      fontWeight: "bold",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    🏛️ Elevation Shots
                  </Typography>
                </StyledAccordionSummary>
                <StyledAccordionDetails>
                  <Card
                    sx={{
                      borderRadius: 2,
                      overflow: "hidden",
                      boxShadow: "0 2px 8px rgba(70, 80, 100, 0.1)",
                    }}
                  >
                    <SlideshowWithModal
                      images={elevationShots}
                      interval={5000}
                      autoPlay={false}
                      width="100%"
                      height="100%"
                      usePercentageCoords={false}
                    />
                  </Card>
                </StyledAccordionDetails>
              </StyledAccordion>
            </Grid>
          )}

          {/* Interior Shots Section */}
          {interiorShots && interiorShots.length > 0 && (
            <Grid item size={{ xs: 12, md: 12 }}>
              <StyledAccordion
                expanded={expanded === "interior"}
                onChange={handleAccordionChange("interior")}
              >
                <StyledAccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="interior-content"
                  id="interior-header"
                >
                  <Typography
                    variant="h5"
                    component="h2"
                    sx={{
                      fontWeight: "bold",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    🏠 Interior Shots
                  </Typography>
                </StyledAccordionSummary>
                <StyledAccordionDetails>
                  <Card
                    sx={{
                      borderRadius: 2,
                      overflow: "hidden",
                      boxShadow: "0 2px 8px rgba(70, 80, 100, 0.1)",
                    }}
                  >
                    <SlideshowWithModal
                      images={interiorShots}
                      interval={5000}
                      autoPlay={false}
                      width="100%"
                      height="100%"
                      usePercentageCoords={false}
                    />
                  </Card>
                </StyledAccordionDetails>
              </StyledAccordion>
            </Grid>
          )}

          {/* Video Section */}
          <Grid item size={{ xs: 12, md: 12 }}>
            <StyledAccordion
              expanded={expanded === "video"}
              onChange={handleAccordionChange("video")}
            >
              <StyledAccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="video-content"
                id="video-header"
              >
                <Typography
                  variant="h5"
                  component="h2"
                  sx={{
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  🎥 Promotional Video
                </Typography>
              </StyledAccordionSummary>
              <StyledAccordionDetails>
                <Card
                  sx={{
                    borderRadius: 2,
                    overflow: "hidden",
                    boxShadow: "0 2px 8px rgba(70, 80, 100, 0.1)",
                    p: 2,
                  }}
                >
                  <LocalVideoPlayer
                    videoSrc={videoMP4}
                    title="Najma Property Video"
                  />
                </Card>
              </StyledAccordionDetails>
            </StyledAccordion>
          </Grid>

          {/* Location Map Section */}
          <Grid item size={{ xs: 12, md: 12 }}>
            <StyledAccordion
              expanded={expanded === "location"}
              onChange={handleAccordionChange("location")}
            >
              <StyledAccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="location-content"
                id="location-header"
              >
                <Typography
                  variant="h5"
                  component="h2"
                  sx={{
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  📍 Our Location
                </Typography>
              </StyledAccordionSummary>
              <StyledAccordionDetails>
                <Card
                  sx={{
                    borderRadius: 2,
                    overflow: "hidden",
                    boxShadow: "0 2px 8px rgba(70, 80, 100, 0.1)",
                  }}
                >
                  <LocationMap
                    latitude={location.latitude}
                    longitude={location.longitude}
                    title={location.title}
                  />
                </Card>
              </StyledAccordionDetails>
            </StyledAccordion>
          </Grid>

          {/* PDF Viewer Section */}
          {pdfUrl && (
            <Grid item xs={12}>
              <StyledAccordion
                expanded={expanded === "pdf"}
                onChange={handleAccordionChange("pdf")}
              >
                <StyledAccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="pdf-content"
                  id="pdf-header"
                >
                  <Typography
                    variant="h5"
                    component="h2"
                    sx={{
                      fontWeight: "bold",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    📄 Document Viewer
                  </Typography>
                </StyledAccordionSummary>
                <StyledAccordionDetails>
                  <Card
                    sx={{
                      borderRadius: 3,
                      overflow: "hidden",
                      boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                    }}
                  >
                    <PDFViewer pdfUrl={pdfUrl} />
                  </Card>
                </StyledAccordionDetails>
              </StyledAccordion>
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
};

export default MediaNpm;
